
import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Cookie, X, Settings2 } from "lucide-react";

const COOKIE_KEY = "cookie-consent";

const DEFAULT_PREFERENCES = {
    necessary: true,
    analytics: false,
    marketing: false,
};

const CATEGORIES = [
    {
        id: "necessary",
        title: "Cookie necessari",
        body: "Questi cookie sono necessari per il corretto funzionamento del sito e non possono essere disattivati.",
        required: true,
    },
    {
        id: "analytics",
        title: "Cookie analitici",
        body: "Utilizzati per comprendere come viene utilizzato il sito e migliorare le prestazioni e l'esperienza degli utenti.",
        required: false,
    },
    {
        id: "marketing",
        title: "Cookie di marketing",
        body: "Utilizzati per eventuali attività di marketing, pubblicità personalizzata e servizi di terze parti.",
        required: false,
    },
];

export default function CookieBanner() {
    const [open, setOpen] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [prefs, setPrefs] = useState(DEFAULT_PREFERENCES);

    useEffect(() => {
        const savedConsent = localStorage.getItem(COOKIE_KEY);

        if (!savedConsent) {
            setOpen(true);
            return;
        }

        try {
            const parsed = JSON.parse(savedConsent);
            setPrefs({
                ...DEFAULT_PREFERENCES,
                ...parsed,
            });
        } catch {
            setOpen(true);
        }
    }, []);

    useEffect(() => {
        const reopen = () => {
            setOpen(true);
            setShowSettings(true);
        };

        window.addEventListener("cookieconsent:reopen", reopen);

        return () => {
            window.removeEventListener("cookieconsent:reopen", reopen);
        };
    }, []);


    const saveConsent = (preferences) => {
        localStorage.setItem(
            COOKIE_KEY,
            JSON.stringify({
                ...preferences,
                savedAt: new Date().toISOString(),
            })
        );

        setPrefs(preferences);
        setOpen(false);
        setShowSettings(false);

        window.dispatchEvent(
            new CustomEvent("cookieconsent:changed")
        );
    };
    
    const acceptAll = () => {
        saveConsent({
            necessary: true,
            analytics: true,
            marketing: true,
        });
    };

    const rejectAll = () => {
        saveConsent({
            necessary: true,
            analytics: false,
            marketing: false,
        });
    };

    const savePreferences = () => {
        saveConsent({
            ...prefs,
            necessary: true,
        });
    };

    if (!open) return null;

    return (
        <AnimatePresence>
            <motion.div
                key="cookie-banner"
                initial={{ y: "110%", opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: "110%", opacity: 0 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                role="dialog"
                aria-modal="true"
                aria-labelledby="cookie-title"
                className="fixed inset-x-0 bottom-0 z-[9999] w-full border-t border-border bg-background/95 shadow-2xl backdrop-blur-xl"
            >
                <div className="mx-auto flex max-w-[1600px] items-start gap-5 px-6 py-6 md:gap-6 md:px-12 md:py-7">

                    {/* Icona */}
                    <div className="hidden h-11 w-11 shrink-0 items-center justify-center rounded-full border border-border text-foreground sm:flex">
                        <Cookie className="h-5 w-5" strokeWidth={1.5} />
                    </div>

                    <div className="flex-1">

                        {/* Header */}
                        <div className="flex items-center justify-between gap-4">
                            <h3
                                id="cookie-title"
                                className="font-display text-lg font-semibold tracking-tight text-foreground"
                            >
                                Rispettiamo la tua privacy
                            </h3>

                            <button
                                type="button"
                                onClick={rejectAll}
                                aria-label="Rifiuta i cookie e chiudi"
                                className="rounded-full p-1 text-muted-foreground transition-colors hover:text-foreground"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>

                        {/* Testo */}
                        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                            Utilizziamo cookie tecnici necessari al funzionamento del sito.
                            Con il tuo consenso possiamo utilizzare anche cookie analitici e
                            di marketing per migliorare la tua esperienza.

                            {" "}

                            <a
                                href="https://www.iubenda.com/privacy-policy/30241697"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-foreground underline underline-offset-4 hover:opacity-70"
                            >
                                Privacy Policy
                            </a>

                            {" · "}

                            <a
                                href="https://www.iubenda.com/privacy-policy/30241697/cookie-policy"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-foreground underline underline-offset-4 hover:opacity-70"
                            >
                                Cookie Policy
                            </a>
                            .
                        </p>

                        {/* Impostazioni */}
                        <AnimatePresence initial={false}>
                            {showSettings && (
                                <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: "auto", opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.3 }}
                                    className="overflow-hidden"
                                >
                                    <div className="mt-5 space-y-3 border-t border-border pt-5">

                                        {CATEGORIES.map((category) => {
                                            const checked =
                                                category.required || !!prefs[category.id];

                                            return (
                                                <label
                                                    key={category.id}
                                                    className="flex cursor-pointer items-start justify-between gap-4 rounded-md border border-border bg-white/[0.02] p-4 transition-colors hover:bg-white/[0.04]"
                                                >
                                                    <div>
                                                        <div className="flex items-center gap-2">
                                                            <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-foreground">
                                                                {category.title}
                                                            </span>

                                                            {category.required && (
                                                                <span className="rounded-full border border-border px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.15em] text-muted-foreground">
                                                                    Sempre attivi
                                                                </span>
                                                            )}
                                                        </div>

                                                        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                                                            {category.body}
                                                        </p>
                                                    </div>

                                                    <span className={`relative mt-1 inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${checked ? "bg-foreground" : "bg-muted"
                                                        } ${category.required ? "opacity-60" : ""}`}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            className="peer sr-only"
                                                            checked={checked}
                                                            disabled={category.required}
                                                            onChange={(event) =>
                                                                setPrefs((previous) => ({
                                                                    ...previous,
                                                                    [category.id]: event.target.checked,
                                                                }))
                                                            }
                                                        />

                                                        <span className={`inline-block h-4 w-4 transform rounded-full bg-background transition-transform ${checked ? "translate-x-6" : "translate-x-1"
                                                            }`}
                                                        />
                                                    </span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Pulsanti */}
                        <div className="mt-5 flex flex-col-reverse items-stretch gap-2 sm:flex-row sm:items-center sm:justify-between">

                            <div className="flex flex-col gap-2 sm:flex-row">

                                <button
                                    type="button"
                                    onClick={() => setShowSettings((value) => !value)}
                                    className="group inline-flex items-center justify-center gap-2 rounded-full border border-border px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] text-muted-foreground transition-colors hover:text-foreground"
                                >
                                    <Settings2 className="h-3.5 w-3.5" />

                                    {showSettings
                                        ? "Nascondi opzioni"
                                        : "Personalizza"}
                                </button>

                                {showSettings && (
                                    <button
                                        type="button"
                                        onClick={savePreferences}
                                        className="rounded-full border border-border px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] text-foreground transition-colors hover:bg-foreground hover:text-background"
                                    >
                                        Salva scelte
                                    </button>
                                )}
                            </div>

                            <div className="flex flex-col gap-2 sm:flex-row">

                                <button
                                    type="button"
                                    onClick={rejectAll}
                                    className="rounded-full border border-border px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] text-foreground transition-colors hover:bg-white/5"
                                >
                                    Rifiuta
                                </button>

                                <button
                                    type="button"
                                    onClick={acceptAll}
                                    className="rounded-full bg-primary px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] text-primary-foreground transition-transform active:scale-95"
                                >
                                    Accetta tutti
                                </button>

                            </div>
                        </div>
                    </div>
                </div>
            </motion.div>
        </AnimatePresence >
    );
}

export function openCookieSettings() {
    window.dispatchEvent(
        new CustomEvent("cookieconsent:reopen")
    );
}
